import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateFAQSchema } from '../../utils/seo';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqCategories = [
    {
      category: 'Shipping',
      questions: [
        {
          question: 'How long does shipping take?',
          answer: 'Processing is typically 2–5 business days after payment confirmation. Delivery is usually 10–20 business days depending on destination and customs. Tracking is provided when shipped.'
        },
        {
          question: 'Do you ship internationally?',
          answer: 'We ship where permitted by local regulations. Contact us with your destination country and we\'ll guide you through the best next steps.'
        },
        {
          question: 'Is packaging discreet?',
          answer: 'Yes—protective, lab-grade packaging with discreet labeling where possible.'
        },
        {
          question: 'What shipping methods do you use?',
          answer: 'We provide door-to-door delivery via FedEx and UPS with double customs clearance to ensure smooth international shipping.'
        },
        {
          question: 'Is cold-chain shipping available?',
          answer: 'Yes, temperature-controlled shipping is available for peptides requiring cold storage. This will be arranged based on product requirements.'
        }
      ]
    },
    {
      category: 'Quality / COA',
      questions: [
        {
          question: 'Do you provide COAs?',
          answer: 'COAs are available on request for most listings. Tell us the product name and we\'ll direct you to the right item and help you request documentation.'
        },
        {
          question: 'What purity are your products?',
          answer: 'Purity is listed on product details where provided; otherwise it\'s shared during the quote process along with documentation.'
        },
        {
          question: 'How do you ensure product purity?',
          answer: 'All products undergo rigorous HPLC testing by third-party laboratories. We maintain strict quality control standards and provide Certificates of Analysis (COA) with every order.'
        },
        {
          question: 'Are your products tested by third parties?',
          answer: 'Yes, all products are tested by independent, accredited laboratories to ensure accuracy and quality.'
        }
      ]
    },
    {
      category: 'Ordering / Quote',
      questions: [
        {
          question: 'How do I order?',
          answer: 'Use "Request Quote" on the product. Share compound, mg quantity, number of vials, and destination country. We confirm availability and issue an invoice via secure payment link.'
        },
        {
          question: 'Can I get a bulk quote?',
          answer: 'Yes. Tell us the compound(s), total quantity, and destination and we\'ll help you submit a bulk quote request.'
        },
        {
          question: 'What is the minimum order quantity (MOQ)?',
          answer: 'Our MOQ is 1 box. We accommodate both small-scale research projects and bulk orders for institutions.'
        },
        {
          question: 'What is the lead time for orders?',
          answer: 'Standard lead time is 5-10 business days from order confirmation to shipment dispatch. Rush orders may be available upon request.'
        }
      ]
    },
    {
      category: 'Payment',
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept BTC, ETH, USDT, Credit Card, and PayPal. Payment options are confirmed after quote approval and issued via secure invoice/payment link. Payment is processed only after you receive and approve your quote.'
        },
        {
          question: 'When do I pay?',
          answer: 'Payment is processed only after you receive and approve your quote. We\'ll send a secure payment link with your invoice. No payment is collected before manual quote approval.'
        },
        {
          question: 'Are payments secure?',
          answer: 'Yes, all payments are processed through secure, encrypted payment links. We never store payment information.'
        },
        {
          question: 'Is there a direct checkout option?',
          answer: 'No. All orders require manual quote approval. We do not offer automated checkout for research compounds. This ensures accurate pricing, availability confirmation, and compliance verification before payment.'
        }
      ]
    },
    {
      category: 'Storage & Handling',
      questions: [
        {
          question: 'How should I store peptides?',
          answer: 'Lyophilized (powder) peptides should be stored at -20°C or colder. Reconstituted peptides should be stored at 2-8°C and used within the recommended timeframe.'
        },
        {
          question: 'What is the shelf life of your products?',
          answer: 'When stored properly, lyophilized peptides typically have a shelf life of 2-3 years. Specific storage instructions are provided with each product.'
        },
        {
          question: 'Do you provide storage guidelines?',
          answer: 'Yes, detailed storage and handling instructions are included with every order and available on product pages.'
        }
      ]
    },
    {
      category: 'Compliance & Legal',
      questions: [
        {
          question: 'Are these products for human consumption?',
          answer: 'No. All products are strictly for laboratory research use only and are not intended for human consumption, clinical use, veterinary use, or therapeutic applications. These are research-grade compounds for qualified researchers and institutions only.'
        },
        {
          question: 'Who can purchase these products?',
          answer: 'Our products are available only to qualified researchers, research institutions, universities, and biotech companies for legitimate laboratory research purposes. By requesting a quote, you confirm you are purchasing for research use only.'
        },
        {
          question: 'Do I need special licenses to purchase?',
          answer: 'Customers should comply with local regulations regarding the purchase and use of research peptides. We recommend verifying requirements in your jurisdiction. Some regions may require institutional credentials or research licenses.'
        },
        {
          question: 'What documentation is provided with orders?',
          answer: 'Each order includes a Certificate of Analysis (COA), product specifications, storage instructions, and shipping documentation.'
        },
        {
          question: 'Do you make any medical or health claims?',
          answer: 'No. We do not make any medical claims, health claims, or therapeutic claims about our products. All products are for research purposes only and are not intended to diagnose, treat, cure, or prevent any disease or medical condition.'
        }
      ]
    },
    {
      category: 'Technical Support',
      questions: [
        {
          question: 'Can you help with product selection?',
          answer: 'Yes, our team can provide guidance on product specifications and suitability for your research applications. Contact us via WhatsApp or email.'
        },
        {
          question: 'Do you offer custom synthesis?',
          answer: 'We may accommodate custom synthesis requests for qualified institutions. Contact us with your specific requirements for evaluation.'
        },
        {
          question: 'How can I contact customer support?',
          answer: 'Reach us via WhatsApp or email at support@shopdailynest.com. We typically respond within 2 hours during business hours.'
        }
      ]
    }
  ];

  // Flatten all FAQs for schema
  const allFAQs = faqCategories.flatMap(category => category.questions);

  useSEO({
    title: 'Research Peptides FAQ | DN Biotech Ordering & Quality Questions',
    description: 'Frequently asked questions about DN Biotech research peptides: shipping, quality, COA documentation, ordering process, payment methods, storage, and compliance information.',
    keywords: 'research peptides FAQ, peptide ordering questions, COA documentation, peptide shipping, research compound compliance',
    canonical: `${siteUrl}/faq`,
    schema: generateFAQSchema(allFAQs)
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-biotech">
        <div className="container-custom text-center">
          <h1 className="text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-200 leading-relaxed max-w-2xl mx-auto">
            Find answers to common questions about our products and services
          </p>
        </div>
      </section>

      {/* Research Use Only Banner */}
      <section className="bg-red-50 border-b-2 border-red-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center space-x-3">
            <i className="ri-alert-line text-red-600 text-xl flex-shrink-0"></i>
            <p className="text-sm font-semibold text-red-900 text-center">
              For Research Use Only. Not for human consumption, medical use, or household application.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-12 last:mb-0">
              <h2 className="text-2xl font-bold text-navy-900 mb-6 pb-3 border-b-2 border-primary-500">
                {section.category}
              </h2>
              <div className="space-y-3">
                {section.questions.map((faq, faqIndex) => {
                  const globalIndex = sectionIndex * 100 + faqIndex;
                  const isOpen = openIndex === globalIndex;
                  
                  return (
                    <div
                      key={faqIndex}
                      className="card overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <h4 className="text-base font-semibold text-navy-900 pr-4">
                          {faq.question}
                        </h4>
                        <div className="w-8 h-8 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                          <i
                            className={`${
                              isOpen ? 'ri-subtract-line' : 'ri-add-line'
                            } text-lg text-primary-600`}
                          ></i>
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5 pt-1">
                          <p className="text-navy-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-sm bg-gradient-subtle">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-navy-600 mb-8 max-w-2xl mx-auto">
            Our team is here to help. Contact us for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/contact"
              className="btn btn-primary btn-lg"
            >
              Contact Us
              <i className="ri-mail-line ml-2"></i>
            </a>
            <a
              href="https://wa.me/639178850200"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-lg bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-500"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}